/**
 * PbMapWidget — Kartenansicht des Kunden-Standorts im PB-Dashboard.
 *
 * Nutzt eine vollständig kostenlose Kartendarstellung:
 *   - Kartenkacheln: OpenStreetMap (https://openstreetmap.org) via Leaflet
 *   - Geocoding (Adresse → Koordinaten): Nominatim (OpenStreetMap), kein API-Key
 *
 * Die Adresse stammt aus dem PB-Profil (strasse/hausnummer/plz/ort/land).
 * Geocoding-Ergebnisse werden modulweit gecacht (1 Request je eindeutiger
 * Adresse), um die Nominatim-Usage-Policy zu respektieren.
 *
 * `mode`:
 *   'compact'  = kompakte Vorschaukarte (kleinere Höhe)
 *   'expanded' = größere eingebettete Karte
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { BRAND, getThemeColors } from '../../lib/theme';
import { primaryProfile, type V1User } from '../../lib/v1';

/* ─── Adresse aus dem Profil aufbereiten ───────────────────────────────────── */

interface ParsedAddress {
  street: string;
  postalcode: string;
  city: string;
  country: string;
  /** Menschlich lesbare Anzeige + stabiler Cache-Key. */
  display: string;
}

function buildAddress(user: V1User | null): ParsedAddress | null {
  if (!user) return null;
  const p = primaryProfile(user);

  // Die Anschrift liegt im CRM 2.0 i.d.R. am User (user.strasse/plz/ort), nicht
  // am Profil. Vorher las das Widget NUR das Profil → „Keine Adresse" trotz
  // vorhandener Anschrift. Jetzt: User zuerst, Profil als Fallback.
  const pick = (a: unknown, b: unknown): string => {
    const av = a == null ? '' : String(a).trim();
    if (av) return av;
    return b == null ? '' : String(b).trim();
  };

  const street = [pick(user.strasse, p?.strasse), pick(user.hausnummer, p?.hausnummer)]
    .filter(Boolean).join(' ').trim();
  const postalcode = pick(user.plz, p?.plz);
  const city = pick(user.ort, p?.ort);
  const country = pick(user.land, p?.land) || 'Deutschland';

  // Ohne mindestens Straße/PLZ/Ort lässt sich nichts sinnvoll geocodieren.
  if (!street && !postalcode && !city) return null;

  const display = [street, [postalcode, city].filter(Boolean).join(' '), country]
    .filter(Boolean)
    .join(', ');

  return { street, postalcode, city, country, display };
}

/* ─── Geocoding via Nominatim (frei, ohne API-Key) ─────────────────────────── */

interface LatLon { lat: number; lon: number }

const geocodeCache = new Map<string, LatLon | null>();

async function geocode(addr: ParsedAddress): Promise<LatLon | null> {
  const key = addr.display;
  if (geocodeCache.has(key)) return geocodeCache.get(key) ?? null;

  const params = new URLSearchParams({ format: 'jsonv2', limit: '1', addressdetails: '0' });
  if (addr.street) params.set('street', addr.street);
  if (addr.postalcode) params.set('postalcode', addr.postalcode);
  if (addr.city) params.set('city', addr.city);
  if (addr.country) params.set('country', addr.country);

  try {
    const res = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`Nominatim ${res.status}`);
    const data = (await res.json()) as Array<{ lat: string; lon: string }>;
    const hit = Array.isArray(data) && data[0]
      ? { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) }
      : null;
    geocodeCache.set(key, hit);
    return hit;
  } catch {
    // Nicht cachen → bei transienten Fehlern erneut versuchen.
    return null;
  }
}

/* ─── Brand-farbiger Pin (divIcon, vermeidet Bundler-Asset-Probleme) ───────── */

const PIN_SVG = `
<svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
     style="filter: drop-shadow(0 2px 3px rgba(0,0,0,0.35));">
  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
        fill="${BRAND}" stroke="#ffffff" stroke-width="1.5"/>
  <circle cx="12" cy="9" r="2.6" fill="#ffffff"/>
</svg>`;

const pinIcon = L.divIcon({
  className: 'pb-map-pin',
  html: PIN_SVG,
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -28],
});

/* ─── Kleiner „Alle anzeigen"-Link (Stil wie im Dashboard) ─────────────────── */

function HeaderLink({ label, onClick }: { label: string; onClick: (e: React.MouseEvent) => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        color: BRAND, fontSize: 12, fontWeight: 600,
      }}
    >
      {label}
    </button>
  );
}

/* ─── Hauptkomponente ──────────────────────────────────────────────────────── */

type Status = 'idle' | 'loading' | 'ok' | 'noaddr' | 'error';

export function PbMapWidget({
  user, dark, mode, onOpenProfile,
}: {
  user: V1User | null;
  dark: boolean;
  mode: 'compact' | 'expanded';
  onOpenProfile?: (e: React.MouseEvent) => void;
}) {
  const { t } = useTranslation();
  const tc = getThemeColors(dark);

  const addr = useMemo(() => buildAddress(user), [user]);
  const addrKey = addr?.display ?? '';

  const mapElRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [coords, setCoords] = useState<LatLon | null>(null);

  const height = mode === 'expanded' ? 360 : 200;

  /* Geocode + Map rendern, wenn sich die Adresse ändert. */
  useEffect(() => {
    let alive = true;

    if (!addr) {
      setStatus('noaddr');
      setCoords(null);
      return;
    }

    setStatus('loading');
    geocode(addr).then((hit) => {
      if (!alive) return;
      if (!hit) { setStatus('error'); setCoords(null); return; }
      setCoords(hit);
      setStatus('ok');
    });

    return () => { alive = false; };
  }, [addr, addrKey]);

  /* Leaflet-Map initialisieren/aktualisieren, sobald Koordinaten vorliegen. */
  useEffect(() => {
    if (status !== 'ok' || !coords || !mapElRef.current) return;

    if (!mapRef.current) {
      const map = L.map(mapElRef.current, {
        zoomControl: true,
        attributionControl: true,
        scrollWheelZoom: false,
      });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(map);
      mapRef.current = map;
    }

    const map = mapRef.current;
    map.setView([coords.lat, coords.lon], 15);

    if (markerRef.current) {
      markerRef.current.setLatLng([coords.lat, coords.lon]);
    } else {
      markerRef.current = L.marker([coords.lat, coords.lon], { icon: pinIcon }).addTo(map);
    }
    if (addr) markerRef.current.bindPopup(addr.display);

    // Container kann anfangs versteckt/0-Höhe sein → Größe neu berechnen.
    const id = window.setTimeout(() => map.invalidateSize(), 60);
    return () => window.clearTimeout(id);
  }, [status, coords, addrKey, height]);

  /* Map bei Unmount aufräumen. */
  useEffect(() => () => {
    mapRef.current?.remove();
    mapRef.current = null;
    markerRef.current = null;
  }, []);

  const osmLink = coords
    ? `https://www.openstreetmap.org/?mlat=${coords.lat}&mlon=${coords.lon}#map=16/${coords.lat}/${coords.lon}`
    : null;

  return (
    <div style={{
      background: tc.card,
      border: `1px solid ${tc.border}`,
      borderRadius: 12,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 14px', borderBottom: `1px solid ${tc.border}`,
      }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: tc.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {t('pflege.detail.dashboard.karte')}
        </span>
        {onOpenProfile && (
          <HeaderLink label={t('pflege.detail.dashboard.zum_profil')} onClick={onOpenProfile} />
        )}
      </div>

      {/* Karten-/Status-Bereich */}
      <div style={{ position: 'relative', flex: 1 }}>
        {status === 'ok' ? (
          <div ref={mapElRef} style={{ width: '100%', height, background: tc.hover }} />
        ) : (
          <div style={{
            height, display: 'flex', alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', padding: 16, color: tc.muted, fontSize: 13,
          }}>
            {status === 'loading' && t('pflege.detail.dashboard.karte_laedt')}
            {status === 'noaddr' && t('pflege.detail.dashboard.karte_keine_adresse')}
            {status === 'error' && t('pflege.detail.dashboard.karte_fehler')}
            {status === 'idle' && '…'}
          </div>
        )}
      </div>

      {/* Footer: Adresse + externer Link */}
      {addr && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
          padding: '10px 14px', borderTop: `1px solid ${tc.border}`,
        }}>
          <span style={{ fontSize: 12, color: tc.heading, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {addr.display}
          </span>
          {osmLink && (
            <a
              href={osmLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ flexShrink: 0, fontSize: 12, fontWeight: 600, color: BRAND, textDecoration: 'none' }}
            >
              {t('pflege.detail.dashboard.karte_oeffnen')}
            </a>
          )}
        </div>
      )}
    </div>
  );
}
